import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Check, X, Zap, ExternalLink, ThumbsUp, Loader } from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import CarryoverPanel from './CarryoverPanel';

const VOTE_LIMIT = 3;

export default function ActionPanel({
  sessionId, teamId, actions, isFacilitator, myParticipantId,
  myVotes, onActionsUpdated, onVotesUpdated, phase
}) {
  const { user } = useAuth();
  const hasJiraAuth = !!user?.cloudId;
  const [manualJiraInput, setManualJiraInput] = useState(null);
  const [manualJiraValue, setManualJiraValue] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newType, setNewType] = useState('action');
  const [submitting, setSubmitting] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiUsed, setAiUsed] = useState(false);
  const [aiDisclaimer, setAiDisclaimer] = useState('');
  const [aiTrend, setAiTrend] = useState('');
  const [slackLoading, setSlackLoading] = useState(false);
  const [slackMsg, setSlackMsg] = useState('');
  const [error, setError] = useState('');

  const [summaryText, setSummaryText] = useState(() => localStorage.getItem(`retro_analysis_${sessionId}`) || '');
  const [summaryGenerated, setSummaryGenerated] = useState(() => !!localStorage.getItem(`retro_analysis_${sessionId}`));
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [channelInput, setChannelInput] = useState('');
  const analysisKey = `retro_analysis_${sessionId}`;
  const [editingActionId, setEditingActionId] = useState(null);
  const [editingContent, setEditingContent] = useState('');

  const pending = actions.filter(a => a.status === 'pending');
  const dismissed = actions.filter(a => a.status === 'dismissed');
  const approved = actions.filter(a => a.status === 'approved');
  const approvedActions = approved.filter(a => a.type === 'action');
  const approvedChanges = approved.filter(a => a.type === 'change');

  const votesUsed = myVotes?.length || 0;
  const votesLeft = VOTE_LIMIT - votesUsed;
  const myVotedIds = new Set((myVotes || []).map(v => v.action_id));

  const submit = async () => {
    if (!newContent.trim()) return;
    setSubmitting(true);
    try {
      let a = await api.actions.create({ session_id: sessionId, participant_id: myParticipantId, submitted_by_name: 'Team', content: newContent.trim(), type: newType });
      a = await api.actions.approve(a.id);
      onActionsUpdated([...actions, a]);
      setNewContent('');
    } catch (e) { setError(e.message); } finally { setSubmitting(false); }
  };

  const deleteAction = async (id) => {
    if (!window.confirm('Remove this item?')) return;
    await api.actions.dismiss(id, 'Removed');
    onActionsUpdated(actions.filter(x => x.id !== id));
  };

  const saveEditAction = async (id) => {
    if (!editingContent.trim()) return;
    try {
      const a = await api.actions.update(id, { content: editingContent.trim() });
      onActionsUpdated(actions.map(x => x.id === id ? a : x));
      setEditingActionId(null);
    } catch (e) { setError(e.message); }
  };

  const changeType = async (id, type) => {
    const a = await api.actions.update(id, { type });
    onActionsUpdated(actions.map(x => x.id === id ? a : x));
  };

  const vote = async (actionId) => {
    if (myVotedIds.has(actionId)) {
      await api.actions.unvote(actionId, { participant_id: myParticipantId, session_id: sessionId });
      onVotesUpdated((myVotes || []).filter(v => v.action_id !== actionId));
      onActionsUpdated(actions.map(a => a.id === actionId ? { ...a, vote_count: Math.max(0, (a.vote_count || 0) - 1) } : a));
    } else {
      if (votesLeft <= 0) { setError('You have used all 3 votes.'); return; }
      await api.actions.vote(actionId, { participant_id: myParticipantId, session_id: sessionId });
      onVotesUpdated([...(myVotes || []), { action_id: actionId, participant_id: myParticipantId }]);
      onActionsUpdated(actions.map(a => a.id === actionId ? { ...a, vote_count: (a.vote_count || 0) + 1 } : a));
    }
  };

  const createJira = async (id) => {
    try {
      const { key } = await api.actions.createJira(id);
      onActionsUpdated(actions.map(a => a.id === id ? { ...a, jira_ticket_key: key } : a));
    } catch (e) { setError(e.message); }
  };

  const saveManualJiraKey = async (id) => {
    const key = manualJiraValue.trim().toUpperCase();
    if (!key) return;
    try {
      const updated = await api.actions.update(id, { jira_ticket_key: key });
      onActionsUpdated(actions.map(a => a.id === id ? { ...a, jira_ticket_key: updated.jira_ticket_key } : a));
      setManualJiraInput(null);
      setManualJiraValue('');
    } catch (e) { setError(e.message || 'Failed to save Jira key'); }
  };

  const runAI = async () => {
    setAiLoading(true);
    setError('');
    try {
      const result = await api.actions.aiAnalyze(sessionId);
      const rawSuggestions = result.suggestions || [];
      const approvedSuggestions = await Promise.all(rawSuggestions.map(s => api.actions.approve(s.id).catch(() => s)));
      onActionsUpdated([...actions, ...approvedSuggestions]);
      setAiUsed(true);
      if (result.disclaimer) setAiDisclaimer(result.disclaimer);
      if (result.trend_analysis) setAiTrend(result.trend_analysis);
    } catch (e) { setError(e.message); } finally { setAiLoading(false); }
  };

  const generateSummary = async () => {
    setSummaryLoading(true);
    setError('');
    try {
      const { summary_text } = await api.actions.generateSummary(sessionId);
      setSummaryText(summary_text);
      setSummaryGenerated(true);
      localStorage.setItem(`retro_analysis_${sessionId}`, summary_text);
    } catch (e) { setError(e.message); } finally { setSummaryLoading(false); }
  };

  const exportSlack = async () => {
    setSlackLoading(true);
    try {
      const extra = {};
      if (summaryText) extra.summary_text = summaryText;
      if (channelInput.trim()) extra.channel_ids = channelInput.split(',').map(c => c.trim()).filter(Boolean);
      const result = await api.actions.slackExport(sessionId, extra);
      setSlackMsg(`Posted to ${result.sent?.length || 1} channel(s) ✓`);
      setTimeout(() => setSlackMsg(''), 4000);
    } catch (e) { setError(e.message); } finally { setSlackLoading(false); }
  };

  return (
    <div>
      {/* Submit action */}
      <div className="card mb-4">
        <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>Submit an Item</h3>
        <div className="flex gap-2 mb-3">
          {['action', 'change'].map(t => (
            <button key={t} className={`btn btn-sm ${newType === t ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setNewType(t)}>
              {t === 'action' ? '⚡ Action' : '🧪 Change'}
            </button>
          ))}
          <span className="text-xs text-muted" style={{ alignSelf: 'center', marginLeft: 8 }}>
            {newType === 'action' ? 'Must-do task' : 'One-sprint experiment'}
          </span>
        </div>
        <div className="flex gap-2">
          <input className="input" value={newContent} onChange={e => setNewContent(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submit()} placeholder="Describe the action or change..." />
          <button className="btn btn-primary btn-sm" onClick={submit} disabled={submitting || !newContent.trim()}>
            <Plus size={14} /> Add
          </button>
        </div>
      </div>

      {/* AI analysis button (discussion phase) */}
      {isFacilitator && phase === 'discussion' && (
        <>
          {/* Carry-over actions from previous retros */}
          <CarryoverPanel
            sessionId={sessionId}
            teamId={teamId}
            isFacilitator={isFacilitator}
            onCarryForward={(a) => onActionsUpdated([...actions, a])}
          />

          {/* FIX-16: AI button greyed after use */}
          {/* Generate Analysis Panel — discuss board feedback, for report/Slack */}
          <div className="card mb-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-bold text-sm">📝 Retro Analysis</div>
                <div className="text-xs text-muted mt-1">AI summary of board feedback for your report & Slack. Does not include action items.</div>
              </div>
              <button className="btn btn-sm btn-secondary flex-shrink-0" onClick={generateSummary} disabled={summaryLoading}>
                {summaryLoading ? '⏳ Generating...' : summaryGenerated ? '↻ Regenerate' : '✨ Generate'}
              </button>
            </div>
            {summaryGenerated && summaryText && (
              <div>
                <textarea className="input" value={summaryText} onChange={e => { setSummaryText(e.target.value); localStorage.setItem(`retro_analysis_${sessionId}`, e.target.value); }}
                  rows={5} style={{ fontFamily: 'inherit', fontSize: 12, marginTop: 6, lineHeight: 1.6 }} />
                <p className="text-xs text-muted mt-1">You can edit this before exporting.</p>
              </div>
            )}
          </div>

          <div className="card mb-4" style={{ background: aiUsed ? 'var(--bg-elevated)' : 'var(--orange-dim)', border: `1px solid ${aiUsed ? 'var(--border)' : 'var(--orange)'}` }}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-sm" style={{ color: aiUsed ? 'var(--text-secondary)' : 'var(--orange)' }}>🤖 AI Action Suggestions</div>
                <div className="text-xs text-muted mt-1">
                  {aiUsed ? 'AI has already run for this session.' : "Claude will analyse Stop items and suggest actions based on your team's playbook."}
                </div>
                {aiTrend && <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>📊 {aiTrend}</div>}
                {aiDisclaimer && aiUsed && <div className="text-xs text-muted mt-1 italic">{aiDisclaimer}</div>}
              </div>
              <button className="btn btn-sm" style={{ background: aiUsed ? 'var(--bg-card)' : 'var(--orange)', color: aiUsed ? 'var(--text-muted)' : 'white', flexShrink: 0 }}
                onClick={runAI} disabled={aiLoading || aiUsed}>
                {aiLoading ? <><Loader size={12} className="pulse" /> Analysing...</> : aiUsed ? '✓ Done' : '✨ Get Suggestions'}
              </button>
            </div>
          </div>
        </>
      )}

      {error && <div className="toast toast-error mb-3">{error}<button className="btn btn-ghost btn-sm" onClick={() => setError('')}>✕</button></div>}

      {/* No dismissed section in discussion/voting - facilitator can delete items */}

      {/* Items list — no pending/approval, everything goes straight to the list */}

      {/* Discussion phase: action items list with edit/delete for facilitator */}
      {phase === 'discussion' && approved.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h4 className="text-sm font-bold mb-3" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--green)' }}>
            Items ({approved.length})
          </h4>
          <div className="flex-col gap-2">
            {approved.map(a => (
              <div key={a.id} className={`action-item type-${a.type}`}>
                <div className="flex items-center gap-2">
                  <div style={{ flex: 1 }}>
                    {editingActionId === a.id ? (
                      <div className="flex gap-1">
                        <input className="input" value={editingContent} onChange={e => setEditingContent(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveEditAction(a.id); if (e.key === 'Escape') setEditingActionId(null); }}
                          style={{ flex: 1, fontSize: 12 }} autoFocus />
                        <button className="btn btn-success btn-sm" onClick={() => saveEditAction(a.id)}>✓</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditingActionId(null)}>✕</button>
                      </div>
                    ) : (
                      <p className="text-sm">{a.content}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`badge ${a.type === 'action' ? 'badge-green' : 'badge-yellow'}`} style={{ fontSize: 9 }}>
                        {a.type === 'action' ? '⚡ Action' : '🧪 Change'}
                      </span>
                      {a.is_ai_suggested && <span className="badge badge-orange" style={{ fontSize: 9 }}>🤖 AI</span>}
                    </div>
                  </div>
                  {isFacilitator && editingActionId !== a.id && (
                    <div className="flex gap-1 flex-shrink-0">
                      <button className="btn btn-ghost btn-icon btn-sm" title="Edit"
                        onClick={() => { setEditingActionId(a.id); setEditingContent(a.content); }}>✏️</button>
                      <button className="btn btn-ghost btn-icon btn-sm" style={{ color: 'var(--red)' }} title="Remove"
                        onClick={() => deleteAction(a.id)}>🗑</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Voting phase: approved items with vote buttons */}
      {phase === 'voting' && approved.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold text-muted" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Vote — {votesLeft} dot{votesLeft !== 1 ? 's' : ''} left
            </h4>
            <div className="vote-dot-row">
              {[0,1,2].map(i => <div key={i} className={`vote-dot ${i < votesUsed ? 'used' : ''}`} />)}
            </div>
          </div>
          <div className="flex-col gap-2">
            {approved.sort((a,b) => b.vote_count - a.vote_count).map(a => (
              <div key={a.id} className={`action-item type-${a.type}`}>
                <div className="flex items-center gap-3">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`badge ${a.type === 'action' ? 'badge-green' : 'badge-yellow'}`} style={{ fontSize: 9 }}>
                        {a.type === 'action' ? '⚡' : '🧪'}
                      </span>
                    </div>
                    {editingActionId === a.id ? (
                      <div className="flex gap-1 mt-1">
                        <input className="input" value={editingContent} onChange={e => setEditingContent(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveEditAction(a.id); if (e.key === 'Escape') setEditingActionId(null); }}
                          style={{ flex: 1, fontSize: 12 }} autoFocus />
                        <button className="btn btn-success btn-sm" onClick={() => saveEditAction(a.id)}>✓</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditingActionId(null)}>✕</button>
                      </div>
                    ) : (
                      <p className="text-sm">{a.content}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isFacilitator && editingActionId !== a.id && (
                      <button className="btn btn-ghost btn-icon btn-sm" title="Edit"
                        onClick={() => { setEditingActionId(a.id); setEditingContent(a.content); }}>✏️</button>
                    )}
                    {a.vote_count > 0 && <span className="badge badge-purple">{a.vote_count}</span>}
                    <button
                      className={`btn btn-sm ${myVotedIds.has(a.id) ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => vote(a.id)}
                      disabled={!myVotedIds.has(a.id) && votesLeft === 0}
                    >
                      <ThumbsUp size={12} /> {myVotedIds.has(a.id) ? 'Voted' : 'Vote'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Results phase: dismissed items (visible to all) */}
      {phase === 'complete' && dismissed.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h4 className="text-xs font-bold text-muted mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Not Actioned ({dismissed.length})
          </h4>
          <div className="flex-col gap-1">
            {dismissed.map(a => (
              <div key={a.id} className="action-item" style={{ opacity: 0.5, background: 'var(--bg-elevated)' }}>
                <div className="flex items-center gap-2">
                  <p className="text-xs line-through flex-1">{a.content}</p>
                  <span className={`badge ${a.type === 'action' ? 'badge-green' : 'badge-yellow'}`} style={{ fontSize: 9 }}>
                    {a.type === 'action' ? '⚡' : '🧪'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Results phase: approved with Jira buttons */}
      {phase === 'complete' && approved.length > 0 && (
        <div>
          {[approvedActions, approvedChanges].map((group, gi) => {
            if (!group.length) return null;
            const label = gi === 0 ? '⚡ Actions' : '🧪 Changes';
            return (
              <div key={gi} style={{ marginBottom: 20 }}>
                <h4 className="text-sm font-bold mb-3" style={{ color: gi === 0 ? 'var(--green)' : 'var(--yellow)' }}>{label}</h4>
                <div className="flex-col gap-2">
                  {group.sort((a,b) => b.vote_count - a.vote_count).map(a => (
                    <div key={a.id} className={`action-item type-${a.type}`}>
                      <div className="flex items-start gap-3">
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p className="text-sm font-bold">{a.content}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {a.vote_count > 0 && <span className="badge badge-purple">{a.vote_count} votes</span>}
                            {a.owner_name && <span className="badge badge-muted">{a.owner_name}</span>}
                            {a.jira_ticket_key && <span className="badge badge-blue font-mono">{a.jira_ticket_key}</span>}
                          </div>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          {isFacilitator && !a.jira_ticket_key && (
                            hasJiraAuth ? (
                              <button className="btn btn-ghost btn-sm" onClick={() => createJira(a.id)} title="Create Jira ticket">
                                <Zap size={12} /> Jira
                              </button>
                            ) : manualJiraInput === a.id ? (
                              <div className="flex gap-1" style={{ alignItems: 'center' }}>
                                <input
                                  className="input"
                                  value={manualJiraValue}
                                  onChange={e => setManualJiraValue(e.target.value)}
                                  placeholder="FA-123"
                                  style={{ fontSize: 11, padding: '2px 6px', height: 26, width: 80 }}
                                  autoFocus
                                  onKeyDown={e => { if (e.key === 'Enter') saveManualJiraKey(a.id); if (e.key === 'Escape') { setManualJiraInput(null); setManualJiraValue(''); } }}
                                />
                                <button className="btn btn-ghost btn-sm" onClick={() => saveManualJiraKey(a.id)} style={{ fontSize: 11, padding: '2px 6px' }}>✓</button>
                                <button className="btn btn-ghost btn-sm" onClick={() => { setManualJiraInput(null); setManualJiraValue(''); }} style={{ fontSize: 11, padding: '2px 6px' }}>✕</button>
                              </div>
                            ) : (
                              <button className="btn btn-ghost btn-sm" onClick={() => { setManualJiraInput(a.id); setManualJiraValue(''); }} title="Link Jira ticket">
                                <Zap size={12} /> Jira
                              </button>
                            )
                          )}
                          {a.jira_ticket_key && (
                            <a href={`https://akeeder.atlassian.net/browse/${a.jira_ticket_key}`} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm">
                              <ExternalLink size={12} />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Export / Slack panel on complete page */}
          {isFacilitator && (
            <div className="card mt-4" style={{ background: 'var(--bg-elevated)' }}>
              <h4 className="text-sm font-bold mb-3">📤 Export &amp; Share</h4>

              {summaryGenerated && summaryText ? (
                <div className="mb-3">
                  <label className="text-xs text-muted mb-1 block">Retro Analysis (editable)</label>
                  <textarea className="input" value={summaryText} onChange={e => { setSummaryText(e.target.value); localStorage.setItem(`retro_analysis_${sessionId}`, e.target.value); }}
                    rows={5} style={{ fontFamily: 'inherit', fontSize: 12, marginBottom: 6, lineHeight: 1.6 }} />
                </div>
              ) : (
                <div className="toast toast-warning mb-3">
                  No analysis generated. Go back to the Discussion phase to generate one, or export without it.
                </div>
              )}

              <label className="text-xs text-muted mb-1 block">Slack Channel IDs (optional, comma-separated)</label>
              <input className="input mb-3" value={channelInput} onChange={e => setChannelInput(e.target.value)}
                placeholder="e.g. C012AB3CD — leave blank for team default" style={{ fontSize: 12 }} />

              <div className="flex gap-2 flex-wrap">
                <button className="btn btn-primary" onClick={exportSlack} disabled={slackLoading}>
                  {slackLoading ? 'Posting...' : '📤 Post to Slack'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => { navigator.clipboard.writeText(summaryText); setSlackMsg('Copied!'); setTimeout(() => setSlackMsg(''), 2000); }}>
                  📋 Copy Analysis
                </button>
              </div>
              {slackMsg && <div className="toast toast-success mt-2">{slackMsg}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
